// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TrialPicture from "./TrialPicture";
import ShapesTrialPicture from "./ShapesTrialPicture";

export type TrialEntryType = {
  candidates: Array<string>;
  correct: boolean;
  disparityArcsec: number;
  expected: string;
  kind: string;
  levelNumber?: number;
  order: number;
  response: string;
  responseTimeSeconds: number;
  seed: number;
  shapes?: Array<string> | null;
  stepId: string;
  targetFamily: string;
};

// "Option_1".."Option_4" are 1-indexed positions in the candidates array (reading order: top
// left, top right, bottom left, bottom right); "NoneStandOut" is the catch answer, index 4.
const optionIndex = (value: string): number => {
  if (value === "NoneStandOut") return 4;
  const match = /^Option_(\d)$/.exec(value);
  return match ? Number(match[1]) - 1 : -1;
};

// Mirrors optionIndex's reading order (top, left, right, bottom); 4 is the catch answer. Only a
// fallback -- the trial's own candidates name the positions for whichever target style it used.
const POSITION_LABELS = ["Top", "Left", "Right", "Bottom"];

// "TopLeft" -> "Top Left", so the SimpleShapes grid positions read like the circle ones.
const spacePosition = (position: string) =>
  position.replace(/([a-z])([A-Z])/g, "$1 $2");

const hasShapes = (
  trial: TrialEntryType
): trial is TrialEntryType & {
  shapes: Array<string>;
} => !!trial.shapes && trial.shapes.length > 0;

type TrialCardType = {
  trial: TrialEntryType;
  cardIndex: number;
  levelIndex: number;
  rungTrialNumber: number | null;
  // Whether a catch trial counts toward its level's verdict, as it does below 200″ from algorithm
  // 1.15.0 on. Earlier catch trials only checked for guessing.
  catchScored?: boolean;
};

// One trial as the patient experienced it: the four candidates (plus "None"), which one
// had depth, and which one the patient picked.
const TrialCard = ({
  trial,
  cardIndex,
  levelIndex,
  rungTrialNumber,
  catchScored = false,
}: TrialCardType) => {
  const { t } = useTranslation();
  const isCatch = trial.kind === "Catch";
  const expectedIndex = optionIndex(trial.expected);
  const responseIndex = optionIndex(trial.response);

  const labelForIndex = (optionIndexValue: number) => {
    if (optionIndexValue === 4) return t("None");
    const position =
      trial.candidates?.[optionIndexValue] ?? POSITION_LABELS[optionIndexValue];
    if (!position) return t("N/A");
    return t(spacePosition(position));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 160px",
        minWidth: 0,
        borderLeft: cardIndex === 0 ? "none" : "1px solid #5B6D7A60",
        borderTop: "1px solid #5B6D7A60",
        overflow: "hidden",
        height: "stretch",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "4px 8px",
          backgroundColor: trial.correct ? "#22BE3920" : "#E10C0C15",
          borderBottom: "1px solid #5B6D7A60",
        }}
      >
        <Typography fontSize="12px" fontWeight={600}>
          {t("Step")} {isCatch ? "X" : rungTrialNumber}
        </Typography>
      </Box>

      <Box sx={{ padding: "8px", backgroundColor: "#F7F8FA" }}>
        {hasShapes(trial) ? (
          <ShapesTrialPicture
            shapes={trial.shapes}
            expectedIndex={expectedIndex}
            responseIndex={responseIndex}
            correct={trial.correct}
          />
        ) : (
          <TrialPicture
            seed={trial.levelNumber ?? levelIndex}
            expectedIndex={expectedIndex}
            responseIndex={responseIndex}
            correct={trial.correct}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "8px",
        }}
      >
        <Box display="flex" justifyContent="space-between" gap="8px">
          <Typography fontSize="12px" color="#5B6D7A">
            {t("Depth")}
          </Typography>
          <Typography fontSize="12px" fontWeight={600}>
            {isCatch ? `${t("None")} (0″)` : `${trial.disparityArcsec}″`}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" gap="8px">
          <Typography fontSize="12px" color="#5B6D7A">
            {t("Answer")}
          </Typography>
          <Typography fontSize="12px" fontWeight={600}>
            {labelForIndex(expectedIndex)}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" gap="8px">
          <Typography fontSize="12px" color="#5B6D7A">
            {t("Picked")}
          </Typography>
          <Typography
            fontSize="12px"
            fontWeight={600}
            color={trial.correct ? "#22BE39" : "#E10C0C"}
          >
            {labelForIndex(responseIndex)}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" gap="8px">
          <Typography fontSize="12px" color="#5B6D7A">
            {t("Time")}
          </Typography>
          <Typography fontSize="12px" fontWeight={600}>
            {trial?.responseTimeSeconds?.toFixed(1)} {t("s")}
          </Typography>
        </Box>
      </Box>

      {isCatch && (
        <Typography
          fontSize="12px"
          color="#5B6D7A"
          textAlign="center"
          sx={{ padding: "0px 8px 8px 8px" }}
        >
          {catchScored
            ? t("Counts towards the plate — checks for guessing")
            : t("Not scored — checks for guessing")}
        </Typography>
      )}
    </Box>
  );
};

export default TrialCard;
